// Delad render-helper för Heritage-ämnessidor (/heritage/<slug>).
// Mönster: createFileRoute("/heritage/<slug>")(heritageTopicRoute(getHeritageTopic("<slug>")!))

import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { Section } from "@/components/site/Section";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { SignatureCta } from "@/components/site/wow/SignatureCta";
import {
  breadcrumbs,
  faqPage,
  qaPage,
  serviceSchema,
  speakable, aeoGeo } from "@/lib/seo/schema";
import { lastmodFor } from "@/lib/seo/sitemap";
import { formatReviewed } from "@/lib/seo/reviewed";
import type { HeritageTopic } from "@/lib/seo/heritageTopics";

const PUBLISHED = "2026-08-17";

type Locale = "sv" | "en";

const UI = {
  sv: {
    reviewedPrefix: "Senast granskad",
    reviewedSuffix: "av Fenny-redaktionen",
    policyLabel: "redaktionell policy",
    cta: "Boka ett första samtal",
    back: "Tillbaka till Heritage",
    process: "Så går det till",
    price: "Prisspann",
    tierLabel: "Nivå",
    tierRange: "Prisspann",
    tierScope: "Vad ingår",
    vat: "Exklusive moms. Exakt pris lämnas som offert efter första samtalet.",
    faq: "Vanliga frågor",
    home: "Hem",
    fallbackRange: "Offert efter första samtal",
  },
  en: {
    reviewedPrefix: "Last reviewed",
    reviewedSuffix: "by the Fenny editorial team",
    policyLabel: "editorial policy",
    cta: "Book a first conversation",
    back: "Back to Heritage",
    process: "How it works",
    price: "Price ranges",
    tierLabel: "Level",
    tierRange: "Price range",
    tierScope: "What's included",
    vat: "Excluding VAT. An exact price is quoted after the first conversation.",
    faq: "Frequently asked questions",
    home: "Home",
    fallbackRange: "Quote after first conversation",
  },
} as const;

export function heritageTopicRoute(t: HeritageTopic, locale: Locale = "sv") {
  const content: HeritageTopic =
    locale === "en" && t.en
      ? { ...t, ...t.en }
      : t;
  const svPath = `/heritage/${t.slug}`;
  const enPath = `/en/heritage/${t.slug}`;
  const path = locale === "en" ? enPath : svPath;
  const absUrl = `https://fenny.se${path}`;
  const reviewed = lastmodFor(svPath) ?? PUBLISHED;
  const ui = UI[locale];
  const hasEn = Boolean(t.en);

  return {
    head: () => ({
      meta: [
        { title: content.metaTitle },
        { name: "description", content: content.metaDescription },
        { property: "og:title", content: content.metaTitle },
        { property: "og:description", content: content.metaDescription },
        { property: "og:type", content: "website" },
        { property: "og:locale", content: locale === "en" ? "en_US" : "sv_SE" },
        { property: "og:url", content: absUrl },
      ],
      links: [
        { rel: "canonical", href: absUrl },
        ...(hasEn
          ? [
            ]
          : []),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(aeoGeo({ path, name: content.metaTitle, description: content.metaDescription, dateModified: reviewed, inLanguage: locale })) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            serviceSchema({
              name: content.serviceName,
              path,
              description: content.metaDescription,
              serviceType: content.serviceType,
              priceRange: content.tiers[0]?.range ?? ui.fallbackRange,
              areaServed: ["Sweden"],
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            qaPage({
              question: content.question,
              answer: content.quickAnswer,
              path,
              authorName: "Fenny",
            }),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(faqPage(content.faq)) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbs([
              { name: ui.home, path: locale === "en" ? "/en" : "/" },
              { name: "Heritage", path: locale === "en" ? "/en/heritage" : "/heritage" },
              { name: content.breadcrumbLabel, path },
            ]),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(speakable(["[data-quick-answer]", "h1"])),
        },
      ],
    }),
    component: () => (
      <HeritageTopicPage t={content} path={path} reviewed={reviewed} locale={locale} />
    ),
  };
}

function HeritageTopicPage({
  t,
  path,
  reviewed,
  locale = "sv",
}: {
  t: HeritageTopic;
  path: string;
  reviewed: string;
  locale?: Locale;
}) {
  const ui = UI[locale];
  const en = locale === "en";
  return (
    <article className="pt-28">
      <Section className="pb-10">
        <div className="max-w-4xl">
          <div className="mb-4 text-xs uppercase tracking-[0.18em] text-mint">
            {t.eyebrow}
          </div>
          <h1 className="text-balance text-4xl font-semibold leading-[1.05] md:text-6xl">
            {t.h1}
          </h1>
          <p className="lede mt-6 max-w-3xl text-pretty text-lg text-foreground/85 md:text-xl">
            {t.lede}
          </p>
          <p className="mt-4 text-sm text-foreground/60">
            {ui.reviewedPrefix} {formatReviewed(reviewed)} {ui.reviewedSuffix} ·{" "}
            <Link to="/redaktionell-policy" className="underline underline-offset-4">
              {ui.policyLabel}
            </Link>
          </p>
          <QuickAnswer className="mt-6" text={t.quickAnswer} tone="mint" />
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to={en ? "/en/contact" : "/kontakt"}
              className="inline-flex items-center gap-2 rounded-full bg-mint px-5 py-3 text-sm font-medium text-background hover:opacity-90"
            >
              {ui.cta} <ArrowRight className="size-4" />
            </Link>
            <Link
              to={en ? "/en/heritage" : "/heritage"}
              className="inline-flex items-center gap-2 rounded-full border border-foreground/20 px-5 py-3 text-sm font-medium hover:bg-foreground/[0.05]"
            >
              {ui.back}
            </Link>
          </div>
        </div>
      </Section>


      <Section className="py-10">
        <div className="max-w-3xl space-y-4 text-lg leading-relaxed text-foreground/80">
          {t.intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">{t.useCasesTitle}</h2>
        <ul className="mt-8 grid gap-3 md:grid-cols-2">
          {t.useCases.map((u) => (
            <li
              key={u}
              className="rounded-xl border border-foreground/10 bg-foreground/[0.02] px-4 py-3 text-foreground/85"
            >
              {u}
            </li>
          ))}
        </ul>
      </Section>

      <Section className="py-10" id="process">
        <h2 className="text-3xl font-semibold md:text-4xl">{ui.process}</h2>
        <ol className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          {t.process.map((s, i) => (
            <li key={s.label} className="rounded-2xl border border-white/10 bg-surface/40 p-5">
              <div className="text-xs uppercase tracking-widest text-mint">
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-2 font-medium">{s.label}</div>
              <p className="mt-2 text-sm text-foreground/75">{s.body}</p>
            </li>
          ))}
        </ol>
      </Section>

      <Section className="py-10" id="pris">
        <h2 className="text-3xl font-semibold md:text-4xl">{ui.price}</h2>
        <div className="mt-8 overflow-hidden rounded-2xl border border-white/10">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.04] text-xs uppercase tracking-wider text-foreground/70">
              <tr>
                <th className="px-4 py-3 text-left font-medium">{ui.tierLabel}</th>
                <th className="px-4 py-3 text-left font-medium">{ui.tierRange}</th>
                <th className="px-4 py-3 text-left font-medium">{ui.tierScope}</th>
              </tr>
            </thead>
            <tbody>
              {t.tiers.map((tier) => (
                <tr key={tier.label} className="border-t border-white/10">
                  <td className="px-4 py-3 font-medium text-foreground">{tier.label}</td>
                  <td className="px-4 py-3 font-medium text-mint">{tier.range}</td>
                  <td className="px-4 py-3 text-foreground/75">{tier.scope}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="mt-4 text-sm text-foreground/65">
          {ui.vat}{" "}
          <Link to={t.relatedPricePath} className="underline underline-offset-4">
            {t.relatedPriceLabel}
          </Link>
          .
        </p>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">{t.notes.title}</h2>
        <ul className="mt-8 grid max-w-3xl gap-3">
          {t.notes.items.map((item) => (
            <li key={item} className="flex gap-3 text-foreground/80">
              <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-mint" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </Section>

      <Section className="py-10">
        <h2 className="text-3xl font-semibold md:text-4xl">{ui.faq}</h2>
        <div className="mt-8 max-w-3xl space-y-6">
          {t.faq.map((f) => (
            <div key={f.q}>
              <h3 className="text-lg font-medium">{f.q}</h3>
              <p className="mt-2 text-foreground/75">{f.a}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="pb-4">
        <RelatedLinksRail sourcePath={path} />
      </Section>
      <SignatureCta />
    </article>
  );
}
